import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fieldMappingSelector } from 'slices/field.slice';
import { fieldFilterValues } from 'utils/fieldUtils';
import { createTypeOptions } from 'utils/objectTypes';
import type { TType } from '@timeedit/types/lib/types';
import {
  Button,
  Filter,
  Icon,
  ObjectFilter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
  Tag,
} from '@timeedit/ui-components';
import { Modal } from '@timeedit/ui-components/antd';
import type { TExactSearchField } from '@timeedit/ui-components/components/ObjectFilter/ObjectFilter.type';
import { useExactSearchFields } from './useExactSearchFields';
import { useOrganizationOptions } from './useOrganizationOptions';
import './ObjectTableFilter.scss';

export interface IObjectTableFilter {
  objectType?: string;
  organization?: string;
  active?: boolean;
  freeTextSearch?: string;
  fields?: TExactSearchField[];
  includeTemplateObjects?: boolean;
}

export interface ObjectTableFilterProps {
  filters: IObjectTableFilter;
  onChange: (filters: IObjectTableFilter) => void;
  availableObjectTypes?: TType[];
  allowTemplateObjects?: boolean;
}

const ObjectTableFilter = ({
  filters,
  onChange,
  availableObjectTypes = [],
  allowTemplateObjects,
}: ObjectTableFilterProps) => {
  const fieldsMap = useSelector(fieldMappingSelector);
  const [modalOpen, setModalOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<IObjectTableFilter>(filters);

  useEffect(() => {
    // Set the local filters to be the same as the filters upon opening the filter modal
    if (modalOpen) {
      setLocalFilters(filters);
    }
  }, [filters, modalOpen]);

  const objectTypeOptions = useMemo(
    () => createTypeOptions(availableObjectTypes),
    [availableObjectTypes]
  );

  const exactSearchFields = useExactSearchFields(
    localFilters.objectType,
    availableObjectTypes
  );

  const {
    options: organizationOptions,
    orgIdByVirtualId,
    virtualIdByOrgId,
  } = useOrganizationOptions();

  const settings = useMemo(
    () => toFilterSettings(filters, virtualIdByOrgId),
    [filters, virtualIdByOrgId]
  );

  const localSettings = useMemo(() => {
    return toFilterSettings(localFilters, virtualIdByOrgId);
  }, [localFilters, virtualIdByOrgId]);

  const onChangeFilter = () => {
    onChange(localFilters);
    setModalOpen(false);
  };

  const handleRemoveFieldFilter = (fieldId: number | string) => {
    const newFields = filters.fields?.filter((f) => f.fieldId !== fieldId);
    setLocalFilters((prev) => ({ ...prev, fields: newFields }));
    onChange({ ...filters, fields: newFields });
  };

  const filterFields = allowTemplateObjects
    ? FILTER_FIELDS_WITH_TEMPLATES
    : FILTER_FIELDS;

  return (
    <div className="object-table-filter">
      <div className="object-table-filter__controls">
        <Filter
          defaultFields={filterFields}
          options={{
            objectType: objectTypeOptions,
            organization: organizationOptions,
          }}
          settings={settings}
          onChange={(settings) =>
            onChange(
              fromFilterSettings(
                settings,
                orgIdByVirtualId,
                localFilters.fields
              )
            )
          }
        />
        <div>
          <Button
            icon={<Icon id="FilterOutlined" alt="Filter" />}
            size="small"
            onClick={() => setModalOpen((prev) => !prev)}
          >
            Advanced Filter
          </Button>
        </div>
      </div>
      {(filters.fields ?? []).length > 0 && (
        <div className="object-table-filter__tags">
          {filters.fields.map((filter) => (
            <Tag
              key={filter.fieldId + filter.values.join(',')}
              closable
              onClose={() => handleRemoveFieldFilter(filter.fieldId)}
            >
              {fieldFilterValues(filter, fieldsMap)}
            </Tag>
          ))}
        </div>
      )}
      <Modal
        onOk={onChangeFilter}
        title="Advanced Filter"
        destroyOnHidden
        width="fit-content"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
      >
        <div className="object-table-filter__modal-content">
          <Filter
            defaultFields={FILTER_FIELDS}
            options={{
              objectType: objectTypeOptions,
              organization: organizationOptions,
            }}
            settings={localSettings}
            onChange={(settings) =>
              setLocalFilters((prev) =>
                fromFilterSettings(settings, orgIdByVirtualId, prev.fields)
              )
            }
          />
          <div>
            <ObjectFilter
              withFilters
              values={localFilters.fields ?? []}
              objectTypeId={Number(localFilters.objectType)}
              onChange={(newFilters) => {
                setLocalFilters((prev) => ({
                  ...prev,
                  fields: newFilters,
                }));
              }}
              filterFieldsOnObjectType={false}
              fields={exactSearchFields}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ObjectTableFilter;

const FILTER_FIELDS: TEIFilterField[] = [
  {
    key: 'objectType',
    label: 'Object Type',
    type: TEEFilterType.treeSelect,
    multiselect: false,
    treeSelectProps: { allowClear: false },
  },
  {
    key: 'organization',
    label: 'Organization',
    type: TEEFilterType.treeSelect,
    multiselect: false,
  },
  {
    key: 'active',
    label: 'Active',
    type: TEEFilterType.boolean,
    multiselect: false,
  },
];

const FILTER_FIELDS_WITH_TEMPLATES: TEIFilterField[] = [
  ...FILTER_FIELDS,
  {
    key: 'includeTemplateObjects',
    label: 'Include Template Objects',
    type: TEEFilterType.boolean,
    multiselect: false,
    selectProps: { allowClear: false },
  },
];

/** IObjectTableFilter → TEIFilter */
const toFilterSettings = (
  filters: IObjectTableFilter,
  virtualIdByOrgId: Record<string, string>
): TEIFilter => ({
  filters: {
    objectType: filters.objectType,
    organization: virtualIdByOrgId[filters.organization],
    active: filters.active,
    includeTemplateObjects: filters.includeTemplateObjects,
  },
  freeTextSearch: filters.freeTextSearch,
});

/** TEIFilter → IObjectTableFilter */
const fromFilterSettings = (
  settings: TEIFilter,
  orgIdByVirtualId: Record<string, string>,
  fields: TExactSearchField[] | undefined
): IObjectTableFilter => ({
  objectType: settings.filters.objectType as string,
  organization: orgIdByVirtualId[settings.filters.organization as string],
  active: settings.filters.active as boolean,
  freeTextSearch: settings.freeTextSearch,
  fields,
  includeTemplateObjects: settings.filters.includeTemplateObjects as boolean,
});
